import { useState, useEffect, useMemo } from 'react';
import { useProduct, useBulkUpdateVariantName, useRemoveVariant, useDisbandVariantGroup } from '../../hooks/useProducts';
import { Button, Modal, ControlledInput, DoubleCheckModal } from '@shared/ui';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { IoTrashOutline } from 'react-icons/io5';
import { notifications } from '@mantine/notifications';
import './ManageVariantsModal.css';
import { Loader } from '@mantine/core';
import '@shared/styles/page.css';

interface ManageVariantsModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: number;
}

interface VariantFormData {
    [key: string]: string | undefined; // variantId -> variantName
}

export default function ManageVariantsModal({
    isOpen,
    onClose,
    productId,
}: ManageVariantsModalProps) {
    const { data: product, isLoading } = useProduct(productId);
    const [removingVariantId, setRemovingVariantId] = useState<number | null>(null);
    const [isDeleteVariantModalOpen, setIsDeleteVariantModalOpen] = useState(false);
    const [variantToDelete, setVariantToDelete] = useState<number | null>(null);
    const [isDisbandGroupModalOpen, setIsDisbandGroupModalOpen] = useState(false);

    const bulkUpdateVariantNameMutation = useBulkUpdateVariantName();
    const removeVariantMutation = useRemoveVariant();
    const disbandVariantGroupMutation = useDisbandVariantGroup();

    const variants = product?.variants || [];

    // Создаем схему валидации на основе текущих вариантов
    const variantSchema = useMemo(() => {
        const shape: Record<string, yup.StringSchema> = {};
        variants.forEach(variant => {
            const fieldName = `variant_${variant.id}`;
            shape[fieldName] = yup.string().required('Название варианта обязательно');
        });
        return yup.object(shape);
    }, [variants]);

    const { control, formState: { errors }, reset, handleSubmit } = useForm<VariantFormData>({
        resolver: yupResolver(variantSchema) as any,
        defaultValues: {},
    });

    // Инициализируем форму при загрузке вариантов
    useEffect(() => {
        if (variants.length > 0) {
            const initialValues: VariantFormData = {};
            variants.forEach(variant => {
                const fieldName = `variant_${variant.id}`;
                const value = variant.variantName || '';
                initialValues[fieldName] = value;
            });
            reset(initialValues);
        }
    }, [variants, reset]);

    // Сбрасываем форму при закрытии модального окна
    useEffect(() => {
        if (!isOpen && variants.length > 0) {
            const initialValues: VariantFormData = {};
            variants.forEach(variant => {
                const fieldName = `variant_${variant.id}`;
                const value = variant.variantName || '';
                initialValues[fieldName] = value;
            });
            reset(initialValues);
        }
    }, [isOpen, variants, reset]);

    const handleSaveAllChanges = handleSubmit(async (data) => {
        const updates = variants.map((variant) => {
            const fieldName = `variant_${variant.id}`;
            return {
                productId: variant.id,
                variantName: (data[fieldName] || '').trim(),
            };
        });

        try {
            await bulkUpdateVariantNameMutation.mutateAsync({ updates });
            notifications.show({
                title: 'Успешно',
                message: 'Названия вариантов обновлены',
                color: 'green',
            });
            onClose();
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при обновлении названий вариантов',
                color: 'red',
            });
        }
    });

    const handleRemoveVariant = (variantId: number) => {
        setVariantToDelete(variantId);
        setIsDeleteVariantModalOpen(true);
    };

    const confirmRemoveVariant = async () => {
        if (!variantToDelete) {
            return;
        }

        setRemovingVariantId(variantToDelete);
        setIsDeleteVariantModalOpen(false);

        try {
            await removeVariantMutation.mutateAsync({
                variantId: variantToDelete,
                productId,
            });
            notifications.show({
                title: 'Успешно',
                message: 'Вариант удален из подборки',
                color: 'green',
            });
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при удалении варианта',
                color: 'red',
            });
            console.error(error);
        } finally {
            setRemovingVariantId(null);
            setVariantToDelete(null);
        }
    };

    const cancelRemoveVariant = () => {
        setIsDeleteVariantModalOpen(false);
        setVariantToDelete(null);
    };

    const handleDisbandGroup = () => {
        setIsDisbandGroupModalOpen(true);
    };

    const confirmDisbandGroup = async () => {
        setIsDisbandGroupModalOpen(false);

        try {
            await disbandVariantGroupMutation.mutateAsync(productId);
            notifications.show({
                title: 'Успешно',
                message: 'Подборка разформирована',
                color: 'green',
            });
            onClose();
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при разформировании подборки',
                color: 'red',
            });
            console.error(error);
        }
    };

    const cancelDisbandGroup = () => {
        setIsDisbandGroupModalOpen(false);
    };

    const getContent = () => {
        if (isLoading) {
            return (
                <div className="empty-container">
                    <Loader />
                </div>
            );
        }

        if (!variants || variants.length === 0) {
            return <div className="empty-container">
                <div>У этого товара нет вариантов</div>;
            </div>
        }

        return (
            <div className="manage-variants-content">
                <div className="manage-variants-list">
                    {variants.map((variant) => {
                        const fieldName = `variant_${variant.id}`;

                        return (
                            <div key={variant.id} className="manage-variant-item">
                                <div className="manage-variant-header">
                                    <div className="manage-variant-name">{variant.name}</div>
                                    <div className="manage-variant-input">
                                        <ControlledInput
                                            name={fieldName}
                                            control={control}
                                            error={errors[fieldName]}
                                        />
                                        <Button
                                            onClick={() => handleRemoveVariant(variant.id)}
                                            disabled={removingVariantId === variant.id}
                                            loading={removingVariantId === variant.id}
                                            variant='secondary-icon'
                                        >
                                            <IoTrashOutline />
                                        </Button>
                                    </div>
                                </div>

                            </div>
                        );
                    })}
                </div>
                <div className="manage-variants-actions">

                    <Button
                        onClick={handleDisbandGroup}
                        disabled={disbandVariantGroupMutation.isPending}
                        loading={disbandVariantGroupMutation.isPending}
                        style={{ background: '#dc3545' }}
                    >
                        Разформировать всю подборку
                    </Button>
                    <Button
                        onClick={handleSaveAllChanges}
                        disabled={bulkUpdateVariantNameMutation.isPending}
                        loading={bulkUpdateVariantNameMutation.isPending}
                    >
                        Сохранить
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Настройка вариантов" maxWidth={800}>
            {getContent()}
            {isDeleteVariantModalOpen && variantToDelete && (
                <DoubleCheckModal
                    isOpen={isDeleteVariantModalOpen}
                    onClose={cancelRemoveVariant}
                    title="Удалить вариант из подборки?"
                    message={`Вы уверены, что хотите удалить этот вариант из подборки?`}
                    secondButtonText="Удалить"
                    secondButtonAction={confirmRemoveVariant}
                    isLoading={removeVariantMutation.isPending}
                />
            )}
            {isDisbandGroupModalOpen && (
                <DoubleCheckModal
                    isOpen={isDisbandGroupModalOpen}
                    onClose={cancelDisbandGroup}
                    title="Разформировать подборку?"
                    message="Вы уверены, что хотите разформировать всю подборку? Все варианты будут удалены из группы."
                    secondButtonText="Разформировать"
                    secondButtonAction={confirmDisbandGroup}
                    isLoading={disbandVariantGroupMutation.isPending}
                />
            )}
        </Modal>
    );
}

